import { useRouter } from 'vue-router';
import type { Ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type { FeatureLike } from 'ol/Feature';

import { useBaseStationsStore } from '@/stores/baseStations';
import { MAP_MODE, FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import type { FeatureType, MapMode } from '@/components/map/types/mapFeatureTypes';

import type { OlMapWithFeaturePick } from '@/components/map/events/useProcessHoverEvent';
import {
	asTypedMapFeature,
	getFeatureId,
} from '@/components/map/utils/mapFeatureGuards';
import type { FeatureId } from '@/components/map/utils/mapFeatureGuards';


type ClickHandler = (clicked: FeatureLike) => RouteLocationRaw;

type FeatureIdHandler = (id: FeatureId) => RouteLocationRaw;

const HOME_MAP_ROUTE: RouteLocationRaw = { name: 'home-map' };

const withFeatureId = (handler: FeatureIdHandler): ClickHandler => clicked => {
	const typed = asTypedMapFeature(clicked);
	if (typed == null) {
		return HOME_MAP_ROUTE;
	}
	const id = getFeatureId(typed.ol);
	if (id == null) {
		return HOME_MAP_ROUTE;
	}
	return handler(id);
};

const processClickBaseStation = (mode: MapMode): FeatureIdHandler => id => {
	const route: RouteLocationRaw = {
		name: 'base-stations-edit',
		params: { id },
	};
	if (mode === MAP_MODE.gnss) {
		route.hash = '#gnss-visibility';
	}
	return route;
};

const processClickBaseline: FeatureIdHandler = id => ({
	name: 'baselines-edit',
	params: { id },
});

const createClickHandlers = (mode: MapMode): Partial<Record<FeatureType, ClickHandler>> => {
	const baselineHandler = withFeatureId(processClickBaseline);
	return {
		[FEATURE_TYPE.baseStation]: withFeatureId(processClickBaseStation(mode)),
		[FEATURE_TYPE.baselineLinkSelfCircle]: baselineHandler,
		[FEATURE_TYPE.baselineLinkLine]: baselineHandler,
		[FEATURE_TYPE.baselineLinkArrowHead]: baselineHandler,
	};
};

const processClickedFeature = (
	clicked: FeatureLike | null | undefined,
	allowedClickTypes: Readonly<Ref<readonly FeatureType[]>>,
	mode: MapMode,
	unhoverAnyThatIsHovered: () => void,
	pushRoute: (route: RouteLocationRaw) => void,
): void => {
	if (clicked == null) {
		return;
	}
	const typed = asTypedMapFeature(clicked);
	if (typed == null || !allowedClickTypes.value.includes(typed.type)) {
		return;
	}
	const clickHandlers = createClickHandlers(mode);
	const processClickFunction = clickHandlers[typed.type];
	if (processClickFunction == null) {
		return;
	}
	unhoverAnyThatIsHovered();
	pushRoute(processClickFunction(clicked));
};

const useProcessClickEvent = (
	olMap: Ref<OlMapWithFeaturePick | null>,
	mapClickEnabled: Readonly<Ref<boolean>>,
	allowedClickTypes: Readonly<Ref<readonly FeatureType[]>>,
	unhoverAnyThatIsHovered: () => void,
) => {
	const router = useRouter();
	const baseStationsStore = useBaseStationsStore();

	const onClickMainMapModes = (event: MapBrowserEvent<UIEvent>): void => {
		if (!mapClickEnabled.value) {
			return;
		}
		const mapInstance = olMap.value;
		if (mapInstance == null) {
			return;
		}
		const clicked = mapInstance.forEachFeatureAtPixel(
			event.pixel,
			feature => feature,
		);
		processClickedFeature(
			clicked,
			allowedClickTypes,
			baseStationsStore.mode,
			unhoverAnyThatIsHovered,
			route => {
				router.push(route);
			},
		);
	};

	return { onClickMainMapModes };
};

export {
	useProcessClickEvent,
};
